import { useMemo, useRef, useState } from 'react'
import Select, { Options } from 'react-select'

import { cx } from '@/utils/strings'
import { TypeComponentCard, TypeLandingPage, TypeSectionSetOfCards } from '@/types/ctf'

type SearchFilterBarProps = {
  sections: TypeLandingPage<'WITHOUT_UNRESOLVABLE_LINKS'>['fields']['sections']
  tags?: string[]
  onSearchChange?: (search: string) => void
  onFilterChange?: (filters: string[]) => void
}

export default function SearchFilterBar({ sections, onSearchChange, onFilterChange }: SearchFilterBarProps) {
  const searchInput = useRef<HTMLInputElement>(null)
  const [search, setSearch] = useState<string>('')
  const [filters, setFilters] = useState<Options<{ id: string; value: string; label: string }>>([])

  const tags = useMemo(() => {
    return (
      sections?.reduce((acc, section, index) => {
        if (!section || index === 0) return acc
        if (section.sys.contentType.sys.id === 'sectionSetOfCards') {
          const setOfCards = section as TypeSectionSetOfCards<'WITHOUT_UNRESOLVABLE_LINKS'>
          setOfCards?.fields?.cards?.forEach((card) => {
            if (card?.sys?.contentType?.sys?.id === 'componentCard') {
              const componentCard = card as TypeComponentCard<'WITHOUT_UNRESOLVABLE_LINKS'>
              componentCard?.fields?.filterTags?.forEach((tag) => {
                if (tag && !acc.includes(tag)) {
                  acc.push(tag)
                }
              })
            }
          })
        }
        return acc
      }, [] as string[]) || []
    )
  }, [sections])

  return (
    <div className="flex items-center justify-between flex-col md:flex-row max-w-8xl mx-auto px-5 lg:px-9 gap-4">
      <form
        className="flex gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          if (!searchInput.current) return
          const value = searchInput.current.value.trim()
          setSearch(value)
          onSearchChange?.(value)
        }}
      >
        <div className="relative">
          <input ref={searchInput} className="pl-6 pr-9 py-3 rounded-full border" type="text" placeholder="Search" />
          <div className={cx('absolute inset-y-0 right-2 flex items-center pr-1', !search.trim() && 'hidden')}>
            <button
              type="button"
              aria-label="Clear search"
              className="font-bwi text-xs text-secondary-400 p-2 "
              onClick={() => {
                setSearch('')
                searchInput.current!.value = ''
                onSearchChange?.('')
              }}
            >
              &#xe93d;
            </button>
          </div>
        </div>
        <button
          type="submit"
          className={cx(
            'rounded-full px-4 py-2 text-primaryBlue border-2 border-primaryBlue',
            'hover:bg-primaryBlue hover:text-white transition-colors ease-in-out duration-300'
          )}
        >
          Search
        </button>
      </form>
      {tags.length > 0 && (
        <Select
          classNames={{
            control: () => `rounded-xl! border h-[52px] min-w-[207px] text-lg px-2`,
            placeholder: () => `text-secondary-900!`,
            indicatorSeparator: () => `hidden`,
            multiValue: () => `rounded-lg! pl-2 bg-secondary-100!`,
            multiValueRemove: () =>
              `rounded-r-lg! pr-2 [&_svg]:hidden after:font-bwi after:content-[""] after:text-secondary-900 after:text-[0.5rem] after:px-1`,
            dropdownIndicator: () =>
              `[&_svg]:hidden after:font-bwi after:content-[""] after:text-secondary-900 after:text-base`,
            menu: () => `rounded-xl!`,
          }}
          isMulti
          name="tags"
          value={filters}
          placeholder="Filter"
          options={tags.map((tag) => ({ id: tag, value: tag, label: tag }))}
          onChange={(selected: Options<{ id: string; value: string; label: string }>) => {
            const value = selected.map((option) => option.value)
            setFilters(selected)
            onFilterChange?.(value)
          }}
          isClearable={false}
        />
      )}
    </div>
  )
}
